import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import adminApi from '../../services/adminApi';
import toast from 'react-hot-toast';
import { EnvelopeIcon, CheckCircleIcon, XMarkIcon, ArchiveBoxIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import ConfirmationModal from '../../components/ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../../hooks/useConfirmationModal';

interface ContactMessageDto {
  id: string;
  name: string;
  email: string;
  category?: string;
  subject: string;
  message: string;
  ipAddress?: string;
  status: number;
  createdAt: string;
  readAt?: string;
  readByAdminId?: string;
  adminNotes?: string;
}

const AdminContactMessagesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedMessage, setSelectedMessage] = useState<ContactMessageDto | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Confirmation modal hook
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();

  // Fetch contact messages
  const { data: contactMessages, isLoading, error } = useQuery({
    queryKey: ['admin-contact-messages', page],
    queryFn: () => adminApi.getContactMessages(page, pageSize),
  });

  // Fetch unread count
  const { data: unreadCount } = useQuery({
    queryKey: ['admin-contact-messages-unread'],
    queryFn: () => adminApi.getContactMessagesUnreadCount(),
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, adminNotes }: { id: string; status: number; adminNotes?: string }) =>
      adminApi.updateContactMessageStatus(id, { status, adminNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages'] });
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages-unread'] });
      toast.success('وضعیت پیام به‌روزرسانی شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در به‌روزرسانی وضعیت پیام');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteContactMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages'] });
      queryClient.invalidateQueries({ queryKey: ['admin-contact-messages-unread'] });
      toast.success('پیام حذف شد');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'خطا در حذف پیام');
    },
  });

  const getStatusBadge = (status: number) => {
    const statusMap: Record<number, { label: string; color: string; icon: React.ReactNode }> = {
      0: { label: 'در انتظار', color: 'bg-yellow-100 text-yellow-800', icon: <EnvelopeIcon className="h-4 w-4" /> },
      1: { label: 'خوانده شده', color: 'bg-blue-100 text-blue-800', icon: <CheckCircleIcon className="h-4 w-4" /> },
      2: { label: 'پاسخ داده شده', color: 'bg-green-100 text-green-800', icon: <CheckCircleIcon className="h-4 w-4" /> },
      3: { label: 'اسپم', color: 'bg-red-100 text-red-800', icon: <ExclamationTriangleIcon className="h-4 w-4" /> },
      4: { label: 'بایگانی شده', color: 'bg-gray-100 text-gray-800', icon: <ArchiveBoxIcon className="h-4 w-4" /> },
    };

    const statusInfo = statusMap[status] || statusMap[0];
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.icon}
        {statusInfo.label}
      </span>
    );
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleViewMessage = async (message: ContactMessageDto) => {
    setSelectedMessage(message);
    setIsViewModalOpen(true);

    // Mark as read if not already read
    if (message.status === 0) {
      updateStatusMutation.mutate({ id: message.id, status: 1 });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4" dir="rtl">
        <p className="text-red-600">خطا در بارگذاری پیام‌های تماس</p>
      </div>
    );
  }

  return (
    <div className="p-6" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">پیام‌های تماس</h1>
            <p className="text-gray-600 mt-2">مدیریت و پاسخ به ارسال‌های فرم تماس</p>
          </div>
          {(unreadCount?.data?.count ?? 0) > 0 && (
            <div className="bg-red-100 text-red-800 px-4 py-2 rounded-lg font-semibold">
              {unreadCount?.data?.count} خوانده نشده
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <EnvelopeIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 0).length || 0}
              </h3>
              <p className="text-gray-600">در انتظار</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 1).length || 0}
              </h3>
              <p className="text-gray-600">خوانده شده</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 2).length || 0}
              </h3>
              <p className="text-gray-600">پاسخ داده شده</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <div className="mr-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {contactMessages?.data?.filter((m: ContactMessageDto) => m.status === 3).length || 0}
              </h3>
              <p className="text-gray-600">اسپم</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">پیام‌های تماس</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">نام</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">ایمیل</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">دسته‌بندی</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">موضوع</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">وضعیت</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">تاریخ</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">عملیات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contactMessages?.data?.map((message: ContactMessageDto) => (
                <tr key={message.id} className={`hover:bg-gray-50 ${message.status === 0 ? 'bg-yellow-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{message.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{message.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{message.category || 'عمومی'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">{message.subject}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(message.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(message.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <button
                        onClick={() => handleViewMessage(message)}
                        className="text-indigo-600 hover:text-indigo-900 transition-colors duration-200"
                      >
                        مشاهده
                      </button>
                      {message.status === 0 && (
                        <button
                          onClick={() => updateStatusMutation.mutate({ id: message.id, status: 1 })}
                          className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                        >
                          علامت خوانده شده
                        </button>
                      )}
                      {message.status !== 3 && (
                        <button
                          onClick={() => updateStatusMutation.mutate({ id: message.id, status: 3 })}
                          className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        >
                          علامت اسپم
                        </button>
                      )}
                      <button
                        onClick={() => {
                          showModal(modalPresets.deleteEn('this message', () => {
                            deleteMutation.mutate(message.id);
                          }));
                        }}
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                      >
                        حذف
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={!contactMessages?.data || contactMessages.data.length < pageSize}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            بعدی
          </button>
          <span className="text-sm text-gray-600">صفحه {page}</span>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            قبلی
          </button>
        </div>
      </div>

      {/* View Message Modal */}
      {isViewModalOpen && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" dir="rtl">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900">پیام تماس</h2>
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedMessage(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">نام</label>
                  <p className="text-gray-900">{selectedMessage.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">ایمیل</label>
                  <p className="text-gray-900">{selectedMessage.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">دسته‌بندی</label>
                  <p className="text-gray-900">{selectedMessage.category || 'پرسش عمومی'}</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">وضعیت</label>
                  {getStatusBadge(selectedMessage.status)}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">تاریخ</label>
                  <p className="text-gray-900">{formatDate(selectedMessage.createdAt)}</p>
                </div>
                {selectedMessage.ipAddress && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">آدرس IP</label>
                    <p className="text-gray-900 text-sm">{selectedMessage.ipAddress}</p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">موضوع</label>
                <p className="text-gray-900">{selectedMessage.subject}</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">پیام</label>
                <div className="bg-gray-50 rounded-lg p-4 text-gray-900 whitespace-pre-wrap">
                  {selectedMessage.message}
                </div>
              </div>
              {selectedMessage.adminNotes && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">یادداشت‌های ادمین</label>
                  <p className="text-gray-600">{selectedMessage.adminNotes}</p>
                </div>
              )}
              <div className="flex justify-end space-x-4 space-x-reverse pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    setSelectedMessage(null);
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  بستن
                </button>
                {selectedMessage.status === 0 && (
                  <button
                    onClick={() => {
                      updateStatusMutation.mutate({ id: selectedMessage.id, status: 1 });
                    }}
                    className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    علامت خوانده شده
                  </button>
                )}
                {selectedMessage.status !== 2 && (
                  <button
                    onClick={() => {
                      updateStatusMutation.mutate({ id: selectedMessage.id, status: 2 });
                    }}
                    className="px-6 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors duration-200"
                  >
                    علامت پاسخ داده شده
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={confirm}
        onClose={cancel}
        title={modalConfig?.title || ''}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText || 'Confirm'}
        cancelText={modalConfig?.cancelText || 'Cancel'}
        type={modalConfig?.type || 'warning'}
      />
    </div>
  );
};

export default AdminContactMessagesPage;

